import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollAnimations';
import { Button } from '@/components/ui/button';
import { GraduationCap, Building2, CheckCircle } from 'lucide-react';

const schoolItems = [
  'School Uniforms & Sportswear',
  'Branded PE Kits & House Colors',
  'Commemorative Items & Awards',
  'Event Materials & Banners',
  'ID Cards & Lanyards',
  'Custom School Merchandise',
];

const corporateItems = [
  'Branded Corporate Uniforms',
  'Executive Gift Sets',
  'Marketing & Promotional Materials',
  'Event Branding & Signage',
  'Custom Merchandise & Swag',
  'Branded Office Supplies',
];

const ForSchoolsCorporates = () => {
  const [tab, setTab] = useState<'schools' | 'corporates'>('schools');
  const { ref, isVisible } = useScrollReveal();

  return (
    <section id="schools" className="py-24 bg-muted/50">
      <div ref={ref} className="container mx-auto px-4">
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Tailored Solutions</span>
          <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 text-foreground">For Schools & Corporates</h2>
        </div>

        {/* Tabs */}
        <div className={`flex justify-center gap-4 mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <button
            onClick={() => setTab('schools')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              tab === 'schools'
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'bg-card text-muted-foreground border border-border hover:border-primary/30'
            }`}
          >
            <GraduationCap className="w-5 h-5" /> For Schools
          </button>
          <button
            onClick={() => setTab('corporates')}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              tab === 'corporates'
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'bg-card text-muted-foreground border border-border hover:border-primary/30'
            }`}
          >
            <Building2 className="w-5 h-5" /> For Corporates
          </button>
        </div>

        {/* Content */}
        <div className={`max-w-5xl mx-auto bg-card rounded-2xl border border-border p-10 shadow-sm transition-all duration-500 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Image */}
            <div className="rounded-xl overflow-hidden aspect-[4/3]">
              <img
                src={tab === 'schools' ? '/images/school-section.jpg' : '/images/corporate-section.png'}
                alt={tab === 'schools' ? 'School uniforms and merchandise' : 'Corporate branding and merchandise'}
                className="w-full h-full object-cover transition-all duration-500"
              />
            </div>

            {/* List */}
            <div>
              <div className="grid gap-4">
                {(tab === 'schools' ? schoolItems : corporateItems).map((item, i) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors"
                    style={{ animation: `slide-up 0.4s ease-out ${i * 80}ms both` }}
                  >
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <Button asChild className="mt-8 w-full bg-primary hover:bg-primary-glow text-primary-foreground">
                <a href="#contact">Book a Consultation</a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ForSchoolsCorporates;
